import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ImportOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Spin } from 'antd';
import dayjs from 'dayjs';
import ExportDocumentsModal from '@/app/employees/_components/actions/ExportDocumentsModal';
import EmployeePreviewModal from '@/app/employees/employee-profile/_components/EmployeePreviewModal';
import { HBars } from '@/components/profile';
import { ROLES_PERMISSIONS, ROUTES } from '@/constants';
import PrivateAction from '@/routes/PrivateAction';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { getUserFullName } from '@/utils/utils';

const initials = (e) =>
  `${e.firstName?.[0] || ''}${e.lastName?.[0] || ''}`.toUpperCase();

/** All employees of one company (the profile filters them in the browser). */
export function useCompanyEmployees(companyId) {
  return useQuery({
    queryKey: [QUERY_KEY.employees, 'company-profile', companyId],
    queryFn: () =>
      API.employees.retrieveDropdownAllEmployees({
        filter: { company: companyId },
        // The list call returns only names; these drive search and contracts.
        select: {
          firstName: 1,
          lastName: 1,
          status: 1,
          createdAt: 1,
          'generalData.position': 1,
          'generalData.personalNumber': 1,
          'employmentSettings.employmentType': 1,
          'employmentSettings.endDate': 1,
        },
        limit: 2000,
      }),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });
}

/**
 * "Employees" tile: find a person of this company, the usual actions on the
 * list (new, import, export) and the people per sector.
 */
export default function CompanyEmployeesPanel({ companyId, stats }) {
  const { t } = useTranslation(['translations', 'menus', 'tables']);
  const tc = (key, opts) =>
    t(`companyProfile.${key}`, { ns: 'translations', ...opts });
  const [search, setSearch] = useState('');
  const [previewId, setPreviewId] = useState(null);
  const { data, isLoading } = useCompanyEmployees(companyId);

  const employeesUrl = `${ROUTES.admin}/${ROUTES.employees}`;
  const hasSectors = (stats?.bySector || []).some((s) => s.name);
  const hires = (stats?.hiresByMonth || [])
    .filter((m) => m.count > 0)
    .map((m) => ({
      label: dayjs(new Date(stats.year, m.month - 1, 1)).format('MMMM'),
      value: m.count,
    }));
  const people = useMemo(() => data?.items || [], [data]);

  // Empty search: the most recently added people.
  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? people.filter((e) =>
          [
            getUserFullName(e.firstName, e.lastName),
            e.generalData?.personalNumber,
            e.generalData?.position,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : [...people].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
    return list.slice(0, 8);
  }, [people, search]);

  const action = (to, icon, label) => (
    <Link to={to}>
      <Button
        className="!h-8 !rounded-[50px] !border-zinc-300 !bg-white !text-zinc-800 hover:!border-accent hover:!text-accent"
        icon={icon}
      >
        {label}
      </Button>
    </Link>
  );

  return (
    <div className="grid gap-4 pt-3">
      <section className="rounded-xl border border-zinc-200 bg-white p-3">
        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined className="text-zinc-400" />}
          placeholder={tc('findEmployee')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="mt-1 px-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-zinc-400">
          {search.trim() ? tc('searchResults') : tc('recentlyAdded')}
        </div>
        {isLoading ? (
          <div className="grid place-items-center py-6">
            <Spin />
          </div>
        ) : results.length ? (
          <ul className="mt-1.5 grid grid-cols-2 gap-1.5 xl:grid-cols-4">
            {results.map((e) => (
              <li key={e._id}>
                <button
                  type="button"
                  onClick={() => setPreviewId(e._id)}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-left transition-colors hover:border-accent hover:bg-[rgba(var(--accentColor),0.06)]"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-[10px] font-bold text-white">
                    {initials(e)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-bold text-zinc-900">
                      {getUserFullName(e.firstName, e.lastName)}
                    </span>
                    <span className="block truncate text-[11px] text-zinc-500">
                      {e.generalData?.personalNumber || '—'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-4 text-center text-[12.5px] text-zinc-400">
            {tc('noMatches')}
          </div>
        )}
      </section>

      <div className="flex flex-wrap gap-2">
        <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.CREATE}>
          {action(
            `${employeesUrl}/${ROUTES.employeeCreate}`,
            <UserAddOutlined />,
            tc('newEmployee')
          )}
          {action(
            `${employeesUrl}/${ROUTES.employeeUpload}`,
            <ImportOutlined />,
            tc('importEmployees')
          )}
        </PrivateAction>
        <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.EXPORT}>
          <ExportDocumentsModal
            companyId={companyId}
            metadata={{
              buttonClassName:
                '!h-8 !rounded-[50px] !border !border-zinc-300 !bg-white !text-zinc-800 hover:!border-accent hover:!text-accent',
            }}
          />
        </PrivateAction>
        {action(
          `${employeesUrl}?company=${companyId}&offset=1&limit=10`,
          <UnorderedListOutlined />,
          tc('openList')
        )}
      </div>

      {/* A company without sectors has everyone in one "no sector" row —
          the headcount again — so it gets the hires per month instead. */}
      {hasSectors ? (
        <section className="rounded-xl border border-zinc-200 bg-white p-3">
          <div className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
            {tc('bySector')}
          </div>
          <HBars
            rows={stats.bySector.map((s) => ({
              label: s.name || tc('noSector'),
              value: s.count,
            }))}
          />
        </section>
      ) : (
        stats && (
          <section className="rounded-xl border border-zinc-200 bg-white p-3">
            <div className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
              {tc('hiresByMonth', { year: stats.year })}
            </div>
            {hires.length ? (
              <HBars rows={hires} />
            ) : (
              <div className="py-2 text-center text-[12.5px] text-zinc-400">
                {tc('noHires', { year: stats.year })}
              </div>
            )}
          </section>
        )
      )}
      <EmployeePreviewModal
        employeeId={previewId}
        onClose={() => setPreviewId(null)}
      />
    </div>
  );
}
